/** @jsx jsx */
import { jsx, Box, Flex, Container } from 'theme-ui';
import React, { FunctionComponent } from 'react';
import { FluidImage as ImageType } from '@codersclan/gatsby-theme-airfleet/src/@types';
import FluidImage from '@codersclan/gatsby-theme-airfleet/src/components/fluidImage';

import BackgroundImage from 'gatsby-background-image';

// Types
import { CTA } from '../../@types/cta';

// Components
import Text, { TEXT_TYPES } from '../text';
import CTAButton from '../ctaButton';
import LineForm from '../lineForm';

// Hooks
import useLinesBg from '../../hooks/useLinesBg';

// Styles
import { baseStyle } from './style';

export interface HeroV2Props {
  title?: string;
  subtitle?: string;
  cardTitle?: string;
  cardSubtitle?: string;
  cardCta?: CTA;
  cardImage?: ImageType;
  signUp?: {
    text: string;
    pardotForm?: {
      pardotEmbedUrl?: string;
    };
    cta: CTA;
  };
}

const HeroV2: FunctionComponent<HeroV2Props> = React.memo(
  ({
    title,
    subtitle,
    cardTitle,
    cardSubtitle,
    cardCta,
    cardImage,
    signUp,
  }) => {
    // Hooks
    const { fluid: fluidBg } = useLinesBg();

    return (
      <div
        sx={{
          backgroundImage: [
            'linear-gradient(to top, #3E3B7C 0.51%, #21244E 42.1%, #0B132B 90.47%)',
            'linear-gradient(to right, #3E3B7C 0.51%, #21244E 42.1%, #0B132B 90.47%)',
          ],
        }}
      >
        <BackgroundImage
          fluid={[fluidBg]}
          sx={{
            backgroundSize: '100%',
            backgroundColor: 'transparent',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'left top',
            '&:before, &:after': {
              display: ['none !important', 'block !important'],
            },
          }}
        >
          <Container>
            <Box sx={baseStyle.mainContainer}>
              {title && (
                <Text type={TEXT_TYPES.H1_BOLD} sx={baseStyle.title}>
                  {title}
                </Text>
              )}
              {subtitle && (
                <Text type={TEXT_TYPES.HERO_SUBHEAD} sx={baseStyle.subtitle}>
                  {subtitle}
                </Text>
              )}
              <Flex sx={baseStyle.cardContainer}>
                <Box sx={baseStyle.cardContainerLeft}>
                  {cardTitle && (
                    <Text
                      type={TEXT_TYPES.H2}
                      sx={baseStyle.cardTitle}
                      lines={['3', '2']}
                    >
                      {cardTitle}
                    </Text>
                  )}
                  {cardImage && (
                    <Box sx={baseStyle.cardResponsiveImageContainer}>
                      <FluidImage
                        {...cardImage}
                        // sx={baseStyle.cardResponsiveImage}
                      />
                    </Box>
                  )}
                  {cardSubtitle && (
                    <Text
                      type={TEXT_TYPES.H5}
                      sx={baseStyle.cardSubtitle}
                      lines="6"
                    >
                      {cardSubtitle}
                    </Text>
                  )}
                  <CTAButton type="link" cta={cardCta} sx={baseStyle.cardCta}>
                    {cardCta?.label}
                  </CTAButton>
                </Box>
                <Flex sx={baseStyle.cardContainerRight}>
                  <Box sx={baseStyle.cardImageContainer}>
                    <FluidImage {...cardImage} sx={baseStyle.cardImage} />
                  </Box>
                </Flex>
              </Flex>
              {signUp && (
                <Box sx={baseStyle.signUpContainer}>
                  <LineForm
                    text={signUp.text}
                    cta={signUp.cta}
                    pardotForm={signUp.pardotForm}
                  />
                </Box>
              )}
            </Box>
          </Container>
        </BackgroundImage>
      </div>
    );
  },
);

export default HeroV2;
